import React, { useState, useEffect } from 'react';
import { getAttendenceData, insertMarkAttendence } from '../../services/dataService';
import { Link, useLocation } from 'react-router-dom';
import { data } from 'jquery';
import toast from 'react-hot-toast';
import { getSecureItem } from '../../services/SecureStorage';

function ApprovedNoPunch() {
  const UID = getSecureItem("userId");
  const location = useLocation();
  const locationData = location.state?.NoPunchApproved || null;

  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedProfitCenter, setSelectedProfitCenter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const departmentIds = getSecureItem("DepartmentId") || ""; // Retrieve the value or default to an empty string
  const DepartmentId = departmentIds.split(",")[0] || null;
  const isAdmin = getSecureItem("IsAdmin");

  // Get yesterday's date as default
  const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Get maximum allowed date (yesterday)
  const getMaxDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const [selectedDate, setSelectedDate] = useState(getYesterday());

  const fetchAttendanceRecords = async (date) => {
    try {
      setLoading(true);

      const payload = {
        Date: date || selectedDate,
        UserId: UID,
      };

      const response = await getAttendenceData(payload);
      console.log("API Response:", response);

      let dataArray = [];

      if (Array.isArray(response)) {
        dataArray = response;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        dataArray = response.data.data;
      } else if (response && typeof response === "object") {
        const possibleArrays = Object.values(response).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          dataArray = possibleArrays[0];
        }
      }

      console.log("Processed data array:", dataArray);

      // Filter for records with no punch in/out and approved status
      const noPunchApproved = dataArray.filter((el) => 
        (el.PUNCHIN === null || el.PUNCHIN === "") &&
        (el.PUNCHOUT === null || el.PUNCHOUT === "") &&
        el.ATTENDANCESTATUS === 1
      );

      const attendanceDetails = isAdmin == 1
        ? noPunchApproved
        : noPunchApproved.filter((el) => el?.DEPARTMENTID == DepartmentId);

      // Initialize attendance data
      setAttendanceData(attendanceDetails);

      // Extract unique departments
      const uniqueDepartments = [
        ...new Set(attendanceDetails.map((item) => item.DepartmentName).filter(Boolean)),
      ];
      setDepartments(uniqueDepartments);

      // Extract unique profit centers
      const uniqueProfitCenters = [
        ...new Set(attendanceDetails.map((item) => item.ProfitcenterName).filter(Boolean)),
      ];
      setProfitCenters(uniqueProfitCenters);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceData([]);
      setDepartments([]);
      setProfitCenters([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchAttendanceRecords(selectedDate);
  }, []);

  // Handle date change
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    fetchAttendanceRecords(newDate);
  };

  // Filter data based on search term, department, and profit center
  useEffect(() => {
    let filtered = attendanceData;

    // Filter by search term (name or code)
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.EmployeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.EmpCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by department
    if (selectedDepartment) {
      filtered = filtered.filter(item => item.DepartmentName === selectedDepartment);
    }

    // Filter by profit center
    if (selectedProfitCenter) {
      filtered = filtered.filter(item => item.ProfitcenterName === selectedProfitCenter);
    }

    setFilteredData(filtered);
  }, [attendanceData, searchTerm, selectedDepartment, selectedProfitCenter]);

  // Calculate pending count (all are approved with status 1)
  const approvedCount = filteredData.length;

  // Format time display
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const time = new Date(timeString);
    return time.toISOString().substr(11, 5);
  };

  return (
    <div className="p-6 bg-white">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Approved No Punch Records
        </h2>

        <Link to={'/'}>
          <button className='btn btn-danger'>
            Back to Dashboard
          </button>
        </Link>
      </div>

      {!loading && attendanceData.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredData.length} of {attendanceData.length} records
          <span className="ml-4">
            | Approved Records: {approvedCount}
          </span>
        </div>
      )}

      {/* Controls Section */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              max={getMaxDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search (Name/Code)
            </label>
            <input
              type="text"
              placeholder="Enter name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Department Filter */}
          {isAdmin == 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Departments</option>
                {departments.map((dept, index) => (
                  <option key={index} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Profit Center Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profit Center
            </label>
            <select
              value={selectedProfitCenter}
              onChange={(e) => setSelectedProfitCenter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Profit Centers</option>
              {profitCenters.map((center, index) => (
                <option key={index} value={center}>
                  {center}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading attendance records...</p>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sl No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                {isAdmin == 1 && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shift Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin == 1 ? "8" : "7"} className="px-6 py-4 text-center text-gray-500">
                    {attendanceData.length === 0
                      ? "No approved no-punch records found."
                      : "No records match the selected filters."}
                  </td>
                </tr>
              ) : (
                filteredData?.map((record, index) => (
                  <tr key={record.ID || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {record.EmployeeName || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.EmpCode || "N/A"}
                    </td>
                    {isAdmin == 1 && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.DepartmentName || "N/A"}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {record.ShiftName || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTime(record.StartTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTime(record.EndTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Approved (No Punch)
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ApprovedNoPunch;